// FIREBASE REMOVED - databaseCleanup kaldırıldı
// import { databaseCleanupManager } from './databaseCleanup';

/**
 * Automated Database Cleanup Scheduler
 * Periyodik database temizliği ve optimizasyonu
 */

interface ScheduleOptions {
  [key: string]: unknown;
}

interface Schedule {
  taskName: string;
  interval: string;
  options: ScheduleOptions;
  lastRun: Date | null;
  nextRun: Date;
  isActive: boolean;
  runCount: number;
  errorCount: number;
}

class CleanupScheduler {
  private schedules: Map<string, Schedule>;
  private isRunning: boolean;
  private lastRun: Date | null;
  private nextRun: Date | null;
  private schedulerInterval: NodeJS.Timeout | null;

  constructor() {
    this.schedules = new Map();
    this.isRunning = false;
    this.lastRun = null;
    this.nextRun = null;
    this.schedulerInterval = null;
  }

  // Schedule cleanup tasks
  scheduleCleanup(taskName: string, interval: string, options: ScheduleOptions = {}): Schedule {
    const schedule = {
      taskName,
      interval,
      options,
      lastRun: null,
      nextRun: this.calculateNextRun(interval),
      isActive: true,
      runCount: 0,
      errorCount: 0
    };

    this.schedules.set(taskName, schedule);
    
    return schedule;
  }

  // Calculate next run time
  calculateNextRun(interval: string): Date {
    const now = new Date();
    const intervals = {
      'hourly': 60 * 60 * 1000,
      'daily': 24 * 60 * 60 * 1000,
      'weekly': 7 * 24 * 60 * 60 * 1000,
      'monthly': 30 * 24 * 60 * 60 * 1000
    };

    const intervalMs = intervals[interval as keyof typeof intervals] || intervals.daily;
    return new Date(now.getTime() + intervalMs);
  }

  // Start the scheduler
  start(): void {
    if (this.isRunning) {
      return;
    }

    this.isRunning = true;

    // Check for scheduled tasks every minute
    this.schedulerInterval = setInterval(() => {
      this.checkScheduledTasks();
    }, 60 * 1000);

    // Initial check
    this.checkScheduledTasks();
  }

  // Stop the scheduler
  stop(): void {
    if (!this.isRunning) {
      return;
    }

    this.isRunning = false;
    if (this.schedulerInterval) {
      clearInterval(this.schedulerInterval);
      this.schedulerInterval = null;
    }

  }

  // Check for tasks that need to run
  async checkScheduledTasks(): Promise<void> {
    const now = new Date();
    
    for (const [taskName, schedule] of this.schedules) {
      if (!schedule.isActive) continue;
      
      if (now >= schedule.nextRun) {
        await this.runScheduledTask(taskName);
      }
    }
  }

  // Run a scheduled task
  async runScheduledTask(taskName: string): Promise<void> {
    const schedule = this.schedules.get(taskName);
    if (!schedule) {
      return;
    }

    try {
      schedule.lastRun = new Date();
      schedule.runCount++;

      // Run the appropriate cleanup task
      let result;
      switch (taskName) {
        case 'daily-cleanup':
          result = await this.runDailyCleanup(schedule.options);
          break;
        case 'weekly-archive':
          result = await this.runWeeklyArchive(schedule.options);
          break;
        case 'monthly-optimization':
          result = await this.runMonthlyOptimization(schedule.options);
          break;
        default:
          // FIREBASE REMOVED - databaseCleanup kaldırıldı
          // result = await databaseCleanupManager.runCleanup();
          result = { message: 'Database cleanup removed - use backend API instead' };
      }

      // Update next run time
      schedule.nextRun = this.calculateNextRun(schedule.interval);
      
      this.logTaskResult(taskName, result);

    } catch (error) {
      schedule.errorCount++;
      
      // If too many errors, disable the task
      if (schedule.errorCount >= 5) {
        schedule.isActive = false;
      }
    }
  }

  // Daily cleanup task
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async runDailyCleanup(_options = {}) {
    
    const results: {
      timestamp: string;
      type: string;
      tasks: Array<{ name: string; [key: string]: unknown }>;
    } = {
      timestamp: new Date().toISOString(),
      type: 'daily-cleanup',
      tasks: []
    };

    // Clean expired cache
    try {
      const cacheCleanup = await this.cleanExpiredCache();
      results.tasks.push({ name: 'cache-cleanup', ...cacheCleanup });
    } catch (error: unknown) {
      results.tasks.push({ name: 'cache-cleanup', error: error instanceof Error ? error.message : 'Unknown error' });
    }

    // Clean temporary files
    try {
      const tempCleanup = await this.cleanTemporaryFiles();
      results.tasks.push({ name: 'temp-cleanup', ...tempCleanup });
    } catch (error: unknown) {
      results.tasks.push({ name: 'temp-cleanup', error: error instanceof Error ? error.message : 'Unknown error' });
    }

    // Clean old logs
    try {
      const logCleanup = await this.cleanOldLogs();
      results.tasks.push({ name: 'log-cleanup', ...logCleanup });
    } catch (error: unknown) {
      results.tasks.push({ name: 'log-cleanup', error: error instanceof Error ? error.message : 'Unknown error' });
    }

    return results;
  }

  // Weekly archive task
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async runWeeklyArchive(_options = {}) {
    
    const results: {
      timestamp: string;
      type: string;
      archived: { transactions?: unknown; smsLogs?: unknown };
    } = {
      timestamp: new Date().toISOString(),
      type: 'weekly-archive',
      archived: {}
    };

    // Archive old transactions
    try {
      const transactionArchive = await this.archiveOldTransactions();
      results.archived.transactions = transactionArchive;
    } catch (error: unknown) {
      results.archived.transactions = { error: error instanceof Error ? error.message : 'Unknown error' };
    }

    // Archive old SMS logs
    try {
      const smsArchive = await this.archiveOldSmsLogs();
      results.archived.smsLogs = smsArchive;
    } catch (error: unknown) {
      results.archived.smsLogs = { error: error instanceof Error ? error.message : 'Unknown error' };
    }

    return results;
  }

  // Monthly optimization task
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async runMonthlyOptimization(_options = {}) {
    
    const results: {
      timestamp: string;
      type: string;
      optimizations: { databaseCleanup?: unknown; indexes?: unknown };
    } = {
      timestamp: new Date().toISOString(),
      type: 'monthly-optimization',
      optimizations: {}
    };

    // FIREBASE REMOVED - databaseCleanup kaldırıldı
    // Run full database cleanup
    try {
      // const fullCleanup = await databaseCleanupManager.runCleanup();
      results.optimizations.databaseCleanup = { message: 'Database cleanup removed - use backend API instead' };
    } catch (error: unknown) {
      results.optimizations.databaseCleanup = { error: error instanceof Error ? error.message : 'Unknown error' };
    }

    // Optimize indexes
    try {
      const indexOptimization = await this.optimizeIndexes();
      results.optimizations.indexes = indexOptimization;
    } catch (error: unknown) {
      results.optimizations.indexes = { error: error instanceof Error ? error.message : 'Unknown error' };
    }

    return results;
  }

  // Clean expired cache
  async cleanExpiredCache() {
    // This would clean localStorage cache
    const cacheKeys = Object.keys(localStorage);
    let cleanedCount = 0;
    
    cacheKeys.forEach(key => {
      if (key.startsWith('cache_')) {
        try {
          const itemStr = localStorage.getItem(key);
          if (!itemStr) return;
          const item = JSON.parse(itemStr);
          if (item && Date.now() - item.timestamp > item.expiration) {
            localStorage.removeItem(key);
            cleanedCount++;
          }
        } catch (error) {
          // Remove invalid cache entries
          localStorage.removeItem(key);
          cleanedCount++;
        }
      }
    });

    return {
      cleanedEntries: cleanedCount,
      message: `Cleaned ${cleanedCount} expired cache entries`
    };
  }

  // Clean temporary files
  async cleanTemporaryFiles() {
    // This would clean temporary files in Firebase Storage
    // For now, return mock data
    return {
      cleanedFiles: 0,
      freedSpace: '0 MB',
      message: 'No temporary files to clean'
    };
  }

  // Clean old logs
  async cleanOldLogs() {
    // This would clean old log entries
    return {
      cleanedLogs: 0,
      message: 'No old logs to clean'
    };
  }

  // Archive old transactions
  async archiveOldTransactions() {
    // This would archive transactions older than 1 year
    return {
      archivedCount: 0,
      message: 'No old transactions to archive'
    };
  }

  // Archive old SMS logs
  async archiveOldSmsLogs() {
    // This would archive SMS logs older than 3 months
    return {
      archivedCount: 0,
      message: 'No old SMS logs to archive'
    };
  }

  // Optimize indexes
  async optimizeIndexes() {
    // This would optimize database indexes
    return {
      optimizedIndexes: 0,
      message: 'No indexes to optimize'
    };
  }

  // Log task result
  logTaskResult(taskName: string, result: unknown) {
    const logEntry = {
      taskName,
      timestamp: new Date().toISOString(),
      result,
      success: typeof result === 'object' && result !== null && 'error' in result ? !(result as any).error : true
    };

    // Store in localStorage for debugging
    const logs = JSON.parse(localStorage.getItem('cleanup-logs') ?? '[]');
    logs.push(logEntry);
    
    // Keep only last 100 logs
    if (logs.length > 100) {
      logs.splice(0, logs.length - 100);
    }
    
    localStorage.setItem('cleanup-logs', JSON.stringify(logs));
  }

  // Get scheduler status
  getStatus() {
    return {
      isRunning: this.isRunning,
      lastRun: this.lastRun,
      nextRun: this.nextRun,
      schedules: Array.from(this.schedules.values()),
      totalTasks: this.schedules.size,
      activeTasks: Array.from(this.schedules.values()).filter(s => s.isActive).length
    };
  }

  // Get task logs
  getLogs(limit = 50) {
    const logs = JSON.parse(localStorage.getItem('cleanup-logs') || '[]');
    return logs.slice(-limit);
  }

  // Manual task execution
  async runTaskNow(taskName: string) {
    if (!this.schedules.has(taskName)) {
      throw new Error(`Task ${taskName} not found`);
    }

    await this.runScheduledTask(taskName);
  }

  // Enable/disable task
  toggleTask(taskName: string, enabled: boolean) {
    const schedule = this.schedules.get(taskName);
    if (schedule) {
      schedule.isActive = enabled;
    }
  }
}

// Create singleton instance
const cleanupScheduler = new CleanupScheduler();

// Default schedules
cleanupScheduler.scheduleCleanup('daily-cleanup', 'daily', {
  runAt: '02:00', // 2 AM
  timezone: 'Europe/Istanbul'
});

cleanupScheduler.scheduleCleanup('weekly-archive', 'weekly', {
  runAt: '03:00', // 3 AM on Sunday
  dayOfWeek: 0
});

cleanupScheduler.scheduleCleanup('monthly-optimization', 'monthly', {
  runAt: '04:00', // 4 AM on 1st of month
  dayOfMonth: 1
});

export default cleanupScheduler;
